import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { AdjustmentRow, AdjustmentSource } from '../../types/closing-adjustments.types';

/**
 * REV-21364 — the Korrekturbuchungen of ONE account (Pure View). Contract:
 * `e2e-lab/account-adjustments.lab.spec.ts`.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * ⚠️ **THIS IS NOT THE KONTOBLATT, AND NOT THE CLOSING-WIDE REGISTER EITHER** — ADR-0015 §9.
 *
 * REV-21364's own technical note guessed it might be the same component as the Kontoblatt
 * (REV-21362). Checked against the prototype: it is not.
 *
 * | | Kontoblatt (21362) | this view |
 * |---|---|---|
 * | rows | every journal line of the account | only the account's CORRECTIONS |
 * | last column | `Saldo`, carried forward | `Status` |
 * | framing rows | `Saldovortrag` / `Schlusssaldo` | none |
 * | time axis | one fiscal year | tabs over THREE, prior years read-only |
 * | row semantics | a comment anchor | status + source + discard |
 *
 * A component with a `mode` input would carry two mutually exclusive column sets, two empty states
 * and two footers — the `mode` would only be a name for "there are two components here".
 *
 * And for the same reason this is not REV-21370 either: different columns, different controls,
 * different width (a narrow pane rather than a full-width register). What the three SHARE is the
 * table look (`_abcp-table.scss`, shared by every ABCP table) and — with REV-21370 — the row type and
 * the status chip.
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * ⚠️ **IT LIVES IN A NARROW PANE, not on a screen** (AC7). Long booking texts and account names are
 * truncated with the full value in the tooltip, and the page NEVER scrolls horizontally.
 *
 * ⚠️ **This surface has no production host in this sprint.** The account workspace it hangs in is
 * REV-21379; it is accepted in the Component Lab. That is the ordering of the tickets, not a gap in
 * this one.
 *
 * **Dumb**: the fiscal year is display state the view may hold (AC8), and everything else is reported.
 * No status, no amount and no row comes into existence here — and **no control here sets `Gebucht`**
 * (AC3).
 */

const SOURCE_LABEL_KEYS: Record<AdjustmentSource, string> = {
    user: 'abcp_adjustments_source_user',
    agent: 'abcp_adjustments_source_agent',
    priorYear: 'abcp_adjustments_source_prior_year',
};

@Component({
    selector: 'abcp-account-adjustments',
    templateUrl: './account-adjustments.component.html',
    styleUrls: ['./account-adjustments.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpAccountAdjustmentsComponent {
    /** Only the corrections ATTRIBUTED to this account (AC1). Already filtered by the host. */
    @Input() adjustments: AdjustmentRow[] = [];

    /** The current fiscal year and the two before it (AC5), newest first. */
    @Input() fiscalYears: number[] = [];

    @Input() selectedFiscalYear: number | null = null;

    /**
     * A PRIOR year — fully read-only (AC5).
     *
     * Served rather than derived, for the same reason as in the closing-wide register: "which year is
     * current" is the backend's fact, and a second copy of it here is a second thing that can be wrong.
     */
    @Input() readOnly = false;

    /**
     * `n offen` — the badge beside the year tabs (AC2).
     *
     * ⚠️ At `0` the badge is GONE, not rendered as "0 offen". The badge answers "is there work here";
     * a zero is the answer "no", and the honest way to render that answer is nothing at all.
     */
    @Input() openCount = 0;

    @Input() loading = false;
    @Input() error = false;

    @Output() fiscalYearChange = new EventEmitter<number>();
    /** Discarding an OPEN correction (AC6). A REPORT — REV-21375 decides whether it takes effect. */
    @Output() discardRequested = new EventEmitter<string>();
    /** A row click. The account workspace (REV-21379) decides what it opens. */
    @Output() adjustmentOpen = new EventEmitter<string>();
    @Output() retryRequested = new EventEmitter<void>();
    /**
     * Enter a NEW correction from inside this account.
     *
     * A report, like every other output here: the view opens no dialog and creates no row. The host
     * owns the entry dialog — the same one the closing-wide register opens — and seeds this account as
     * the attribution, so a correction entered here is still listed here whichever way the postings go.
     */
    @Output() createRequested = new EventEmitter<void>();

    /**
     * Six columns: Buchungstext · GK · GK Bezeichnung · Soll · Haben · Status.
     *
     * ⚠️ **No `Datum`** (prototype change 03.09.2026). It was the seventh, and it went for the reason
     * the MWST column went before it: this pane is a narrow column inside an account workspace, and
     * the date is the field the reader is least likely to have come for. The closing-wide register
     * still carries it, and so does the detail surface — which is where a date is actually compared.
     *
     * The prototype's ledger has an eighth, `MWST` — and it belongs to the KONTOBLATT, whose rows are
     * ERP journal lines that really do carry a VAT code. A hand-entered correction does not: the entry
     * dialog has no VAT field (the prototype's has none either) and the entity refuses to invent one,
     * so the cell was empty on every row it could ever render. A column that is structurally blank
     * reads as missing data rather than as an absent fact.
     */
    readonly columnCount = 6;
    readonly skeletonRows = [1, 2, 3, 4, 5];

    get isEmpty(): boolean {
        return this.adjustments.length === 0;
    }

    /**
     * Placeholders only while there is nothing to show yet. A reload keeps the rows it has until the new
     * ones arrive: placeholders over old rows read as two states at once, and swapping the rows for
     * placeholders on every refresh (a poll, a reload after a write) makes the table flash.
     */
    get showSkeleton(): boolean {
        return this.loading && this.adjustments.length === 0;
    }

    /** The badge is rendered only above zero, and never in a prior year (AC2, AC5). */
    get showOpenBadge(): boolean {
        return !this.readOnly && this.openCount > 0;
    }

    /**
     * Entry is offered in the CURRENT year only — a prior year is reference, not a workspace (§10).
     *
     * Same rule the discard control follows, and the same reason it is a `get` rather than a template
     * expression: `readOnly` is the server's answer about the year, and there must be one reading of it.
     */
    get canCreate(): boolean {
        return !this.readOnly;
    }

    /**
     * The GEGENKONTO of a row — the account on the OTHER side from this one.
     *
     * ⚠️ This is why the view needs `ownerAccountId`-shaped data rather than just two accounts: the
     * column is `GK`, and which of the two accounts is the counter-account depends on which account's
     * pane the row is being rendered in. The attribution account is this pane's account, so the counter
     * account is whichever of debit/credit is NOT it.
     */
    counterAccount(row: AdjustmentRow): { number: string; name: string } {
        const own = row.owner?.id ?? row.debit.id;

        return row.debit.id === own ? row.credit : row.debit;
    }

    /**
     * The `Soll` cell — the amount, but only when THIS pane's account is the debit side.
     *
     * `null` renders an EMPTY cell, and empty is the point (AC9): a ledger reader tells a debit from a
     * credit by which of the two columns carries a figure, so a `0.00` in the other one would destroy
     * the very distinction the two columns exist for.
     */
    debitAmount(row: AdjustmentRow): number | null {
        const own = row.owner?.id ?? row.debit.id;

        return row.debit.id === own ? row.amount.minor : null;
    }

    /** The `Haben` cell — the mirror of {@link debitAmount}. */
    creditAmount(row: AdjustmentRow): number | null {
        const own = row.owner?.id ?? row.debit.id;

        return row.credit.id === own ? row.amount.minor : null;
    }

    sourceLabelKey(source: AdjustmentSource): string {
        return SOURCE_LABEL_KEYS[source];
    }

    /** A prior-year row's chip reads `Gebucht <Jahr>` (§10). */
    bookedYearOf(row: AdjustmentRow): number | null {
        return row.source === 'priorYear' ? this.selectedFiscalYear : null;
    }

    /**
     * Whether the discard control is offered on a row (AC6).
     *
     * `editable` is the BACKEND's answer, where the rule lives (§7). Absent at `Übertragen` and
     * `Gebucht`, and absent for every row in a prior year.
     */
    canDiscard(row: AdjustmentRow): boolean {
        return !this.readOnly && row.editable;
    }

    isSelectedYear(year: number): boolean {
        return year === this.selectedFiscalYear;
    }

    trackById(_index: number, row: { id: string }): string {
        return row.id;
    }

    trackBySelf<T extends string | number>(_index: number, value: T): T {
        return value;
    }

    onYearClick(year: number): void {
        this.fiscalYearChange.emit(year);
    }

    onRowClick(row: AdjustmentRow): void {
        this.adjustmentOpen.emit(row.id);
    }

    onDiscardClick(row: AdjustmentRow): void {
        if (this.canDiscard(row)) {
            this.discardRequested.emit(row.id);
        }
    }

    onCreateClick(): void {
        this.createRequested.emit();
    }

    onRetryClick(): void {
        this.retryRequested.emit();
    }
}
